import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { onlyBranch } from '@/lib/singleBranch';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthedImage } from '@/components/shared/AuthedImage';
import { Upload, X } from 'lucide-react';

// Canonical participant create/edit form — the ONLY UI path to POST /participants and PUT /participants/{id}.
// Used by ParticipantListPage (Add / pencil Edit) and the Participant Profile (header + NDIS Details link).
export const EMPTY_PARTICIPANT_FORM = {
  ndis_number: '', first_name: '', middle_name: '', last_name: '', preferred_name: '',
  date_of_birth: '', gender: '', pronouns: '',
  phone: '', alternate_phone: '', email: '',
  address: '', state_id: '', branch_id: '', funding_type: '',
};

export const NDIS_NUMBER_RE = /^\d{9}$/; // mirrors backend rule (exactly 9 digits); backend remains authoritative

export const participantToForm = (p) => ({
  ndis_number: p?.ndis_number || '', first_name: p?.first_name || '', middle_name: p?.middle_name || '', last_name: p?.last_name || '',
  preferred_name: p?.preferred_name || '', date_of_birth: p?.date_of_birth || '', gender: p?.gender || '', pronouns: p?.pronouns || '',
  phone: p?.phone || '', alternate_phone: p?.alternate_phone || '', email: p?.email || '', address: p?.address || '',
  state_id: p?.state_id || '', branch_id: p?.branch_id || '', funding_type: p?.funding_type || '',
});

export function ParticipantEditDialog({ open, onOpenChange, participant = null, states: statesProp, branches: branchesProp, onSaved }) {
  const editingId = participant?.id || null;
  const [form, setForm] = useState({ ...EMPTY_PARTICIPANT_FORM });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState(statesProp || []);
  const [branches, setBranches] = useState(branchesProp || []);
  useEffect(() => {   // one branch: fill it in (the picker is hidden)
    const only = onlyBranch(branches);
    if (only && !form.branch_id) setForm((f) => ({ ...f, branch_id: only.code }));
  }, [branches, form.branch_id]);
  const [ndisError, setNdisError] = useState('');
  const [formError, setFormError] = useState('');

  // UA-6-F13: reset ONLY on the open transition (closed → open). The previous dependency on `participant` re-ran this
  // reset on every background refetch of the participant while the dialog was open, wiping the typed values.
  const wasOpen = useRef(false);
  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    setForm(participant ? participantToForm(participant) : { ...EMPTY_PARTICIPANT_FORM });
    setPhotoFile(null);
    setPhotoPreview(participant?.profile_photo_url || null);
    setNdisError('');
    if (!statesProp || !branchesProp) {
      Promise.all([api.get('/org/states'), api.get('/org/branches')])
        .then(([s, b]) => { setStates(s.data.data || []); setBranches(b.data.data || []); })
        .catch(() => {});
    }
  }, [open, participant, statesProp, branchesProp]);

  useEffect(() => { if (statesProp) setStates(statesProp); }, [statesProp]);
  useEffect(() => { if (branchesProp) setBranches(branchesProp); }, [branchesProp]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Maximum 5MB'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhoto = async (id) => {
    if (!photoFile || !id) return;
    try {
      const fd = new FormData();
      fd.append('file', photoFile);
      await api.post(`/participants/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch { /* photo upload is optional */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // UA-6-F13: in-app required-field message for BOTH create and edit (the form is noValidate — a native browser bubble
    // was the only feedback in edit mode and is invisible/suppressed inside the dialog).
    const missing = [];
    if (!String(form.ndis_number || '').trim()) missing.push('NDIS Number');
    if (!form.date_of_birth) missing.push('Date of Birth');
    if (!String(form.first_name || '').trim()) missing.push('First Name');
    if (!String(form.last_name || '').trim()) missing.push('Last Name');
    if (!String(form.phone || '').trim()) missing.push('Phone');
    if (!String(form.email || '').trim()) missing.push('Email');
    if (missing.length) { setFormError(`Please fill required fields: ${missing.join(', ')}.`); toast.error(`Please fill required fields: ${missing.join(', ')}.`); return; }
    setFormError('');
    const ndis = String(form.ndis_number || '').trim();
    if (ndis && !NDIS_NUMBER_RE.test(ndis)) {
      setNdisError('NDIS number must be exactly 9 digits.');
      toast.error('NDIS number must be exactly 9 digits.');
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/participants/${editingId}`, { ...form, ndis_number: ndis });
        await uploadPhoto(editingId);
        toast.success('Participant updated');
        onOpenChange(false);
        onSaved?.({ mode: 'edit', id: editingId });
        return;
      }
      const res = await api.post('/participants', { ...form, ndis_number: ndis });
      const newId = res.data.data.id;
      await uploadPhoto(newId);
      toast.success('Participant created');
      onOpenChange(false);
      onSaved?.({ mode: 'create', id: newId });
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'INVALID_NDIS_NUMBER' || code === 'NDIS_NUMBER_ALREADY_EXISTS') setNdisError(err.response.data.error.message);
      setFormError(err.response?.data?.error?.message || err.response?.data?.detail?.message || 'Changes could not be saved — the dialog stays open so nothing typed is lost.');   // UA-6-F13
      notify.error(err, 'SAVE_FAILED');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="participant-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit Participant' : 'Create Participant'}</DialogTitle>
          <DialogDescription>{editingId ? 'Update this NDIS participant\u2019s details' : 'Register a new NDIS participant'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="participant-form">
          {formError && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="participant-form-error">{formError}</p>}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>NDIS Number *</Label>
              <Input value={form.ndis_number} onChange={(e) => { setForm({ ...form, ndis_number: e.target.value }); setNdisError(''); }} required inputMode="numeric" maxLength={9}
                className={`font-mono ${ndisError ? 'border-destructive' : ''}`} aria-invalid={!!ndisError} data-testid="participant-ndis-input" />
              {ndisError ? <p className="text-xs text-destructive" data-testid="participant-ndis-error">{ndisError}</p> : <p className="text-xs text-muted-foreground">9 digits</p>}
            </div>
            <div className="space-y-2">
              <Label>Date of Birth *</Label>
              <TrinitiDatePicker value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="participant-dob" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="participant-firstname-input" />
            </div>
            <div className="space-y-2">
              <Label>Middle Name</Label>
              <Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="participant-lastname-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preferred Name</Label>
              <Input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} placeholder="Nickname or preferred name" />
            </div>
            <div className="space-y-2">
              <Label>Pronouns</Label>
              <Select value={form.pronouns} onValueChange={(v) => setForm({ ...form, pronouns: v })}>
                <SelectTrigger><SelectValue placeholder="Select pronouns" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="he/him">He/Him</SelectItem>
                  <SelectItem value="she/her">She/Her</SelectItem>
                  <SelectItem value="they/them">They/Them</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non_binary">Non-Binary</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer Not to Say</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="participant-phone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Alternate Phone</Label>
              <Input value={form.alternate_phone} onChange={(e) => setForm({ ...form, alternate_phone: e.target.value })} placeholder="Mobile or secondary phone" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="participant-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Profile Photo</Label>
            <div className="flex items-center gap-4">
              {photoPreview ? (
                <div className="relative">
                  <AuthedImage src={photoPreview} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
                  <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" data-testid="remove-photo-btn">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <div>
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 transition-colors" data-testid="upload-photo-btn">
                  <Upload className="w-4 h-4" />
                  {photoFile ? 'Change Photo' : 'Upload Photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handlePhotoSelect} data-testid="photo-file-input" />
                </label>
                <p className="text-xs text-slate-500 mt-1">JPEG, PNG, WebP or GIF. Max 5MB</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Funding Type</Label>
            <Select value={form.funding_type} onValueChange={(v) => setForm({ ...form, funding_type: v })}>
              <SelectTrigger data-testid="participant-funding-type"><SelectValue placeholder="Select funding type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
                <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
                <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
                <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={form.state_id} onValueChange={(v) => setForm({ ...form, state_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!onlyBranch(branches) && (   /* one branch: filled in automatically, no picker */
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="participant-submit-button">
            {loading ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : 'Create Participant')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
